<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card :loading="loading" class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip v-if="!embedded" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="cancel">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                Fermer
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="save">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                Sauvegarder
              </v-tooltip>
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="createNewPanne">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                Créer une panne
              </v-tooltip>
            </div>
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>

    <!-- PANELS -->
    <v-expansion-panels>
      <!-- INFORMATIONS -->
      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Informations </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row align="stretch">
            <!-- Left section -->
            <v-col cols="8">
              <v-row density="compact">
                <v-col cols="6">
                  <v-autocomplete v-model="product.productMake" :items="productMakes" label="Fabricant *" />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.productType" :items="productTypes" label="Type *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.aquaNb" type="number" label="N° aqua *" />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.productModelId" :items="productModels" item-title="model"
                    item-value="id" label="Modèle *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.price" type="number" label="Prix d'achat" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="product.serialNb" label="N° Série *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.bonEntreeDate" type="date" label="Date Bon d'entrée" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="product.bonEntreeNb" label="N° Bon d'entrée" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.compteur" type="number" label="Compteur" />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.productStatus" :items="productStatus" label="Status *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-autocomplete v-model="product.reservationStatus" :items="reservationStatus"
                    label="Disponibilité *" />
                </v-col>
                <v-col cols="6" v-if="isDestructedReservationStatus">
                  <v-text-field v-model="product.destructionDate" type="date" label="Date de destruction *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-autocomplete v-model="product.laverieId" :items="laveries" item-title="numeroDeContrat"
                    item-value="id" label="Laverie" clearable />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.storageDepotId" :items="storageDepots" item-title="name"
                    item-value="id" label="Dépôt" clearable />
                </v-col>
              </v-row>
            </v-col>

            <!-- Right section -->
            <v-col cols="4" class="right-section d-flex flex-column">
              <!-- Similar available products -->
              <div class="text-subtitle-2 font-weight-medium mb-1 text-primary">Produits disponibles similaires ({{
                similarProducts.length }})</div>
              <div class="right-section-scroll">
                <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
                <div v-else-if="similarProducts.length === 0" class="text-body-2 text-medium-emphasis">
                  Aucun produit disponible
                </div>
                <v-list v-else density="compact" class="pa-0">
                  <v-list-item v-for="p in similarProducts" :key="p.id" class="bg-grey-lighten-3 mb-1 rounded"
                    style="cursor: pointer" @click="router.push({ name: 'product', params: { id: p.id } })">
                    <v-list-item-title class="text-body-2 font-weight-medium">N° {{ p.aquaNb }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{ p.serialNb }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Notifications </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-switch v-model="product.notificationEnabled" color="primary" label="Activer les notifications" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANNES -->
      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Pannes </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-list>
            <v-list-item v-for="p in pannes" :key="p.id" class="bg-grey-lighten-2 my-2">
              <v-list-item-title class="text-blue">
                {{ p.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <div class="d-flex flex-column ga-4">
                  <div>
                    <div v-if="p.desc" class="text-body-2 text-medium-emphasis">
                      {{ p.desc }}
                    </div>
                    <div class="mt-2 text-body-2">
                      <div><strong>Date de création:</strong> {{ formatDate(p.creationDate) }}</div>
                      <div><strong>Réparée:</strong> {{ p.repaired ? "Oui" : "Non" }}</div>
                      <div v-if="p.repaired"><strong>Date de réparation:</strong> {{ formatDate(p.reparationDate) }}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="text-subtitle-2 font-weight-medium mb-1">Pièces installées</div>
                    <div v-if="!p.installedSpareParts || p.installedSpareParts.length === 0"
                      class="text-body-2 text-medium-emphasis">
                      Aucune pièce installée
                    </div>
                    <v-list v-else density="compact" class="bg-transparent pa-0">
                      <v-list-item v-for="sparePart in p.installedSpareParts" :key="sparePart.id" class="px-0">
                        <v-list-item-title>
                          {{ sparePart.displayName || sparePart.sparepartId }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          Quantite: {{ sparePart.quantity }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </div>
                </div>
              </v-list-item-subtitle>

              <template #append>
                <div class="d-flex ga-2">
                  <v-btn icon color="teal" @click="edit(p)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="cyan" @click="remove(p)">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Context Error Codes -->
      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Codes d'erreur </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

          <v-list v-else-if="contextErrorCodes.length > 0" density="compact">
            <v-list-item v-for="(errorCode, index) in contextErrorCodes"
              :key="errorCode.id || `${errorCode.code}-${index}`">
              <v-list-item-title class="font-weight-medium bg-grey-lighten-3 px-2 py-1 rounded">{{ errorCode.message
              }}</v-list-item-title>

              <div class="ml-2">
                <v-list-item-subtitle class="text-body-2 mt-1">
                  Code AccuTrac: {{ errorCode.accuTracCode || "-" }}
                </v-list-item-subtitle>

                <v-list-item-subtitle class="text-body-2 mt-1">
                  Code Machine: {{ errorCode.code || "-" }}
                </v-list-item-subtitle>

                <div v-if="errorCode.description" class="text-body-2 text-medium-emphasis mt-1">
                  Description: {{ errorCode.description }}
                </div>

                <div v-if="(errorCode.possibleCauses?.length ?? 0) > 0" class="mt-2">
                  <div class="text-caption font-weight-bold">Causes possibles</div>
                  <ul class="pl-4 mb-0">
                    <li v-for="(cause, causeIndex) in errorCode.possibleCauses"
                      :key="`cause-${errorCode.id || index}-${causeIndex}`" class="text-body-2">
                      {{ cause }}
                    </li>
                  </ul>
                </div>

                <div v-if="(errorCode.recommendedActions?.length ?? 0) > 0" class="mt-2">
                  <div class="text-caption font-weight-bold">Actions recommandées</div>
                  <ul class="pl-4 mb-0">
                    <li v-for="(action, actionIndex) in errorCode.recommendedActions"
                      :key="`action-${errorCode.id || index}-${actionIndex}`" class="text-body-2">
                      {{ action }}
                    </li>
                  </ul>
                </div>
              </div>

            </v-list-item>
          </v-list>

          <div v-else class="text-medium-emphasis">
            Aucun code d'erreur disponible.
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title v-slot="{ expanded }">
          <v-row dense>
            <v-col cols="4" class="text-primary">Historique</v-col>
            <v-col cols="8" class="text-medium-emphasis">
              <v-fade-transition leave-absolute>
                <span v-if="expanded" key="0"> </span>
                <span v-else key="1">
                  <div v-if="product">
                    {{ historyPanelText }}
                  </div>
                </span>
              </v-fade-transition>
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row dense v-for="(he, index) in historyElements" :key="index">
            <v-col cols="4">
              <v-text-field type="text" :value="he.name" :disabled="true"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field label="In Date" v-model="he.inDate" prepend-icon="mdi-calendar" :disabled="true">
              </v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field label="Out Date" v-model="he.outDate" prepend-icon="mdi-calendar"
                :disabled="true"></v-text-field>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <ConfirmDialog v-model="removePanneDialog" :dialog-title="dialogTitle" :dialog-text="dialogText"
      @confirm="onConfirm" @cancel="onCancel" confirm-button-text="Confirmer" cancel-button-text="Annuler" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import { ProductMake, ProductStatus, ProductType, ReservationStatus } from "@/types";
import { getProductById, postProduct, putProduct, getFilteredProducts } from "@/utils/product-api";
import { deletePanne, getPannesByProduct } from "@/utils/panne-api";
import { getHistoryElementsByProduct } from "@/utils/product-history-element-api";
import { fetchFilteredProductDiagnosticContexts } from "@/utils/product-diagnostic-context-api";
import { getAllLaveries } from "@/utils/laverie-api";
import { getAllStorageDepots } from "@/utils/storage-depot-api";
import { fetchFilteredProductModels } from "@/utils/product-model-api";

import type { Product } from "@/types";
import type { Panne } from "@/types";
import type { Laverie } from "@/types";
import type { StorageDepot } from "@/types";
import type { ErrorCode } from "@/types";
import { HistoryElement } from "@/types";
import { ProductModel } from "@/types/product-model";

const router = useRouter();
const route = useRoute();
const props = withDefaults(defineProps<{
  productId?: string;
  embedded?: boolean;
}>(), {
  embedded: false,
});
const emit = defineEmits<{
  (event: "close"): void;
  (event: "saved", product: Product): void;
}>();
const appStore = useAppStore();

const loading = ref(false);
const hydratingProduct = ref(false);
const title = ref("");
const product = ref<Product>(createEmptyProduct());
const pannes = ref<Panne[]>([]);
const laveries = ref<Laverie[]>([]);
const storageDepots = ref<StorageDepot[]>([]);
const historyElements = ref<HistoryElement[]>([]);
const contextErrorCodes = ref<ErrorCode[]>([]);
const similarProducts = ref<Product[]>([]);
const productModels = ref<ProductModel[]>([]);

const removePanneDialog = ref(false);
const selectedPanne = ref<Panne | null>(null);

const dialogTitle = "Suppression";
const dialogText = "Voulez-vous supprimer cette panne ?";

const productStatus = Object.values(ProductStatus);
const reservationStatus = Object.values(ReservationStatus);
const productTypes = Object.values(ProductType);
const productMakes = Object.values(ProductMake);

const getProductId = () => props.productId ?? (route.params.id as string | undefined);
const isCreatingNewProduct = () => route.name === "newproduct" || !getProductId();

const isDestructedReservationStatus = computed(
  () => product.value.reservationStatus === ReservationStatus.DETRUIT
);

const historyPanelText = computed(() => {
  return historyElements.value?.length ?? 0;
});

watch(
  () => product.value.productMake,
  async (productMake, previousProductMake) => {
    if (!hydratingProduct.value && productMake !== previousProductMake) {
      product.value.productModelId = undefined;
      product.value.model = "";
    }

    if (!productMake) {
      productModels.value = [];
      return;
    }

    try {
      productModels.value = await fetchFilteredProductModels(productMake);
    } catch (e) {
      productModels.value = [];
    }
  },
  { immediate: true }
);

watch(
  () => product.value.laverieId,
  (v) => {
    if (hydratingProduct.value) return;
    if (v) {
      product.value.reservationStatus = ReservationStatus.RESERVED;
      product.value.storageDepotId = null;
    }
  }
);

watch(
  () => product.value.storageDepotId,
  (v) => {
    if (hydratingProduct.value) return;
    if (v) {
      product.value.reservationStatus = ReservationStatus.AVAILABLE;
      product.value.laverieId = null;
    }
  }
);

watch(
  [() => product.value.productMake, () => product.value.productModelId, () => product.value.productType
  ],
  async ([productMake, productModelId, productType]) => {
    // loadProduct() loads similar products itself once the product is hydrated
    if (hydratingProduct.value) return;
    if (!productMake || !productModelId || !productType) {
      similarProducts.value = [];
      return;
    }
    try {
      loading.value = true;
      const results = await getFilteredProducts([ReservationStatus.AVAILABLE], productMake as ProductMake, productType as ProductType, undefined, productModelId);
      similarProducts.value = results.filter((p) => p.id !== product.value.id);
    } catch (e) {
      similarProducts.value = [];
    } finally {
      loading.value = false;
    }
  },
  { immediate: true }
);

watch(
  [() => product.value.productMake, () => product.value.productType],
  async ([productMake, productType]) => {
    if (!productMake || !productType) {
      contextErrorCodes.value = [];
      return;
    }

    try {
      loading.value = true;
      const contexts = await fetchFilteredProductDiagnosticContexts(productMake, productType);
      contextErrorCodes.value = contexts.flatMap((context) => context.errorCodes || []);
    } catch (e) {
      contextErrorCodes.value = [];
      // Ignore because it's handled in axios service
    } finally {
      loading.value = false;
    }
  },
  { immediate: true }
);

function formatDate(date?: string) {
  if (!date) {
    return "-";
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString("fr-FR");
}

function createEmptyProduct(): Product {
  return {
    id: undefined,
    laverieId: undefined,
    storageDepotId: undefined,
    model: "",
    price: 0,
    aquaNb: 0,
    serialNb: "",
    compteur: 0,
    bonEntreeNb: 0,
    bonEntreeDate: "",
    destructionDate: undefined,
    notificationEnabled: true,
    productMake: undefined,
    productType: undefined,
    productStatus: undefined,
    reservationStatus: undefined,
  } as Product;
}

const save = async () => {
  loading.value = true;
  try {
    if (!product.value.id) {
      product.value = await postProduct(product.value);
      appStore.sendSuccessNotice("New product has been added.");
      router.replace({ name: "product", params: { id: product.value.id } });
      title.value = "Editer le produit";
    } else {
      await putProduct(product.value.id, product.value);
      appStore.sendSuccessNotice("Product has been updated.");
    }
    emit("saved", product.value);
    historyElements.value = await getHistoryElementsByProduct(product.value.id);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

const cancel = () => {
  if (props.embedded) {
    emit("close");
    return;
  }
  router.push({ name: "products" });
};

const edit = (p: Panne) =>
  router.push({ name: "panne", params: { productId: product.value.id, id: p.id } });

const remove = (p: Panne) => {
  selectedPanne.value = p;
  removePanneDialog.value = true;
};

const onConfirm = async () => {
  if (!selectedPanne.value) return;
  await deletePanne(selectedPanne.value.id);
  pannes.value = await getPannesByProduct(product.value.id);
  removePanneDialog.value = false;
};

const onCancel = () => {
  removePanneDialog.value = false;
  selectedPanne.value = null;
};

const createNewPanne = () => {
  if (route.params.id) {
    router.push({ name: "newpanne", params: { productId: route.params.id } });
  }
};

async function loadSimilarProducts() {
  const productMake = product.value.productMake;
  const productModelId = product.value.productModelId;
  const productType = product.value.productType;

  if (!productMake || !productModelId || !productType) {
    similarProducts.value = [];
    return;
  }

  try {
    const results = await getFilteredProducts([ReservationStatus.AVAILABLE], productMake as ProductMake, productType as ProductType, undefined, productModelId);
    similarProducts.value = results.filter((p) => p.id !== product.value.id);
  } catch (e) {
    similarProducts.value = [];
  }
}

async function loadProduct(productId: string) {
  try {
    loading.value = true;
    title.value = "Editer le produit";

    const [p, pn, he] = await Promise.all([
      getProductById(productId),
      getPannesByProduct(productId),
      getHistoryElementsByProduct(productId),
    ]);
    hydratingProduct.value = true;
    product.value = p;
    await nextTick();
    hydratingProduct.value = false;

    pannes.value = pn;
    historyElements.value = he;

    // Load similar products after product is hydrated
    await loadSimilarProducts();
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

watch(
  () => route.params.id,
  async (id) => {
    if (id) {
      await loadProduct(id as string);
    }
  }
);

watch(
  () => props.productId,
  async (productId) => {
    if (productId) {
      await loadProduct(productId);
    }
  }
);

onMounted(async () => {
  try {
    loading.value = true;
    [laveries.value, storageDepots.value] = await Promise.all([
      getAllLaveries(),
      getAllStorageDepots(),
    ]);
    if (isCreatingNewProduct()) {
      title.value = "Nouveau produit";
      return;
    }

    const productId = getProductId();
    if (productId) {
      await loadProduct(productId);
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.right-section {
  min-height: 0;
  overflow: hidden;
}

.right-section-scroll {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow-y: auto;
}
</style>
